import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, MapPin, CheckCircle2 } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import {
  BRANCH_LABEL,
  REGION_LABEL,
  type ThaiProvince,
} from "@/content/provinces-th/labels";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { pickNearby } from "@/lib/nearby-links";

/**
 * Region-based nearby picks plus the list-adjacent provinces, so every province
 * page is guaranteed reciprocal inbound links (no thin/orphan province hubs).
 * Computed in the loader so the 77-province corpus never ships in the entry
 * bundle.
 */
function nearbyProvinces(all: ThaiProvince[], p: ThaiProvince): ThaiProvince[] {
  const i = all.findIndex((x) => x.slug === p.slug);
  const adjacent = [
    all[(i - 1 + all.length) % all.length],
    all[(i + 1) % all.length],
  ].filter((x) => x && x.slug !== p.slug);
  const picks = pickNearby(all, p, (x) => x.region, 5);
  const seen = new Set<string>();
  return [...adjacent, ...picks].filter((x) => {
    if (seen.has(x.slug)) return false;
    seen.add(x.slug);
    return true;
  });
}
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { PROVINCE_SERVICES, PROVINCE_SERVICE_SLUGS } from "./$province.$service";

const CORE_SERVICES = [
  { slug: "notary-public", label: "Notary Public" },
  { slug: "certified-translation", label: "แปลรับรอง" },
  { slug: "mfa-consular", label: "MFA Consular Legalization" },
  { slug: "marriage-thailand", label: "จดทะเบียนสมรสไทย" },
  { slug: "marriage-abroad", label: "จดทะเบียนสมรสต่างประเทศ" },
  { slug: "police-clearance", label: "ตรวจประวัติอาชญากรรม" },
  { slug: "work-permit", label: "Work Permit" },
  { slug: "long-term-visas", label: "วีซ่าระยะยาว DTV/LTR" },
];

function buildFaqs(p: ThaiProvince) {
  return [
    {
      q: `อยู่${p.nameTh} ใช้บริการ Notary และแปลรับรองได้ไหม?`,
      a: `ได้ครับ — Thai Notary ครอบคลุมครบ 77 จังหวัดผ่าน${BRANCH_LABEL[p.nearestBranch]} ส่งภาพเอกสารผ่านไลน์ ทีมรับรองและจัดส่งผ่าน Kerry/Flash ถึง${p.nameTh}ภายใน 1–3 วันทำการ`,
    },
    {
      q: `ที่${p.nameTh} ต้องเดินทางมาเซ็นเองที่ออฟฟิศไหม?`,
      a: `ไม่จำเป็นสำหรับเอกสารที่ไม่ต้องเซ็นต่อหน้าทนาย (เช่น แปลเอกสารราชการ) — สำหรับเอกสาร Notary ที่ต้องเซ็นต่อหน้า สามารถนัด Video Notary ผ่าน Zoom หรือทีมเดินทาง on-site ตามตกลง`,
    },
    {
      q: `ค่าจัดส่งจาก${BRANCH_LABEL[p.nearestBranch]}ถึง${p.nameTh}เท่าไหร่?`,
      a: `ค่าจัดส่ง Kerry/Flash อยู่ที่ประมาณ 50–150 บาทขึ้นกับน้ำหนัก แจ้งราคารวมก่อนยืนยันงานเสมอ — รวมประกันพัสดุและ tracking number ทุกครั้ง`,
    },
    {
      q: `ที่${p.nameTh} รับบริการอะไรบ้าง?`,
      a: `Notary Public, แปลรับรอง 30+ ภาษา, MFA Consular Legalization, สถานทูตทุกประเทศ, ตรวจประวัติอาชญากรรม, จดทะเบียนสมรสไทย/ต่างชาติ และวีซ่าไทย/ต่างประเทศ ครบจบที่เดียว`,
    },
    {
      q: `รับเคสด่วนสำหรับลูกค้า${p.nameTh}ได้ไหม?`,
      a: `ได้ครับ — เคสด่วนสามารถทำเอกสารรอ Same-Day ที่${BRANCH_LABEL[p.nearestBranch]} แล้วส่ง Flash Express ภายในวันเดียวกัน หรือลูกค้ารับเองที่ออฟฟิศก็ได้`,
    },
    {
      q: `ออกใบเสร็จ/ใบกำกับภาษีสำหรับเบิกบริษัทใน${p.nameTh}ได้ไหม?`,
      a: `ได้ครับ — บริษัทจดทะเบียนถูกต้อง ออกใบกำกับภาษีเต็มรูปแบบ ส่งทางอีเมลหรือไปรษณีย์ตามต้องการ พร้อมหัก ณ ที่จ่ายตามกฎหมาย`,
    },
  ];
}

export const Route = createFileRoute("/provinces/$province/")({
  loader: async ({
    params,
  }): Promise<{ province: ThaiProvince; nearby: ThaiProvince[]; crossLinks: ThaiProvince[] }> => {
    const { getProvinceBySlug, THAI_PROVINCES } = await import(
      "@/content/provinces-th/registry"
    );
    const p = getProvinceBySlug(params.province);
    if (!p) throw notFound();
    return {
      province: p,
      nearby: nearbyProvinces(THAI_PROVINCES, p),
      crossLinks: pickNearby(THAI_PROVINCES, p, (x) => x.region, 4),
    };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.province;
    if (!p) {
      return pageHeadForProvince({
        title: "ไม่พบจังหวัด",
        description: "ไม่พบจังหวัดนี้",
        path: `/provinces/${params.province}`,
      });
    }
    const site = getActiveSiteUrl();
    const title = `Notary & แปลรับรอง ${p.nameTh} — ส่งถึงบ้าน | Thai Notary`;
    const desc = `บริการ Notary Public, แปลรับรอง 30+ ภาษา, MFA และวีซ่า สำหรับลูกค้า${p.nameTh} — รับ-ส่งเอกสารผ่าน Kerry/Flash จาก${BRANCH_LABEL[p.nearestBranch]} ภายใน 1–3 วันทำการ`;
    const base = pageHeadForProvince({ title, description: desc, path: `/provinces/${p.slug}` });
    const faqs = buildFaqs(p);
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "77 จังหวัด", url: `${site}/provinces` },
              { name: p.nameTh, url: `${site}/provinces/${p.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${site}/provinces/${p.slug}#localbusiness`,
            name: `Thai Notary — บริการใน${p.nameTh}`,
            image: `${site}/og-image.jpg`,
            url: `${site}/provinces/${p.slug}`,
            telephone: COMPANY.phone,
            priceRange: "฿฿",
            address: {
              "@type": "PostalAddress",
              addressLocality: p.nameEn,
              addressRegion: REGION_LABEL[p.region],
              addressCountry: "TH",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: p.geo.lat,
              longitude: p.geo.lng,
            },
            areaServed: {
              "@type": "AdministrativeArea",
              name: p.nameTh,
              alternateName: p.nameEn,
            },
            openingHours: "Mo-Su 08:00-20:00",
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/provinces/${params.province}`)) },
      ],
    };
  },
  component: ProvincePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบจังหวัดนี้</h1>
        <Link to="/provinces" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับรายการ 77 จังหวัด
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function ProvincePage() {
  const data = Route.useLoaderData() as {
    province: ThaiProvince;
    nearby: ThaiProvince[];
    crossLinks: ThaiProvince[];
  };
  const p = data.province;
  const { nearby, crossLinks } = data;
  const faqs = buildFaqs(p);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/provinces" className="hover:text-primary">77 จังหวัด</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{p.nameTh}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              <MapPin className="mr-1 inline h-3.5 w-3.5" />
              {REGION_LABEL[p.region]}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              บริการ Notary, แปลรับรอง & MFA สำหรับลูกค้า{p.nameTh}
            </h1>
            <p id="ai-answer-tldr" className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">สรุปสั้น</span>
              อยู่{p.nameTh} ({p.nameEn})? Thai Notary ดูแลคุณผ่าน{BRANCH_LABEL[p.nearestBranch]} —
              ส่งภาพเอกสารผ่านไลน์ ทีมประเมินและรับรอง พร้อมจัดส่งกลับถึงบ้านผ่าน Kerry/Flash ภายใน 1–3 วันทำการ
              รองรับ Notary, แปลรับรอง 30+ ภาษา, MFA Consular Legalization, สถานทูต และวีซ่าไทย/ต่างประเทศ
              ครบจบที่เดียวเริ่มต้น 1,500 บาท
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">สาขาที่ดูแล</div>
              <div className="mt-1 text-sm font-bold">{BRANCH_LABEL[p.nearestBranch]}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">เวลาส่งเอกสาร</div>
              <div className="mt-1 text-sm font-bold">1–3 วันทำการ (Kerry/Flash)</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">ค่าจัดส่ง</div>
              <div className="mt-1 text-sm font-bold">50–150 บาท · พร้อม tracking</div>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">บริการ Long-tail สำหรับ{p.nameTh} (มีหน้าเฉพาะ)</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              {PROVINCE_SERVICE_SLUGS.map((slug) => {
                const s = PROVINCE_SERVICES[slug];
                return (
                  <Link
                    key={slug}
                    to="/provinces/$province/$service"
                    params={{ province: p.slug, service: slug }}
                    className="group rounded-2xl border border-gold/30 bg-gold/5 p-5 transition-all hover:-translate-y-0.5 hover:border-gold"
                  >
                    <h3 className="text-base font-bold text-primary group-hover:text-gold">{s.label} {p.nameTh}</h3>
                    <p className="mt-2 text-xs text-muted-foreground">เริ่ม ฿{s.priceFrom.toLocaleString()} · {s.duration} →</p>
                  </Link>
                );
              })}
            </div>
            <h2 className="mt-10 text-2xl font-bold">บริการอื่นที่ลูกค้า{p.nameTh}ใช้บ่อย</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {CORE_SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to="/services/$service"
                  params={{ service: s.slug }}
                  className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <h3 className="text-base font-bold text-primary group-hover:text-gold">{s.label}</h3>
                  <p className="mt-2 text-xs text-muted-foreground">ดูราคา ขั้นตอน เอกสาร →</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="defer-paint border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">จังหวัดใกล้เคียงใน{REGION_LABEL[p.region]}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              ลูกค้าที่{p.nameTh}มักเทียบเวลาจัดส่งกับจังหวัดข้างเคียงในภาคเดียวกัน — กดดูหน้าเฉพาะจังหวัดได้เลย
            </p>
            <div className="mt-5 grid gap-3 md:grid-cols-3 lg:grid-cols-5">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to="/provinces/$province"
                  params={{ province: n.slug }}
                  className="rounded-xl border border-border bg-card p-4 text-sm font-semibold text-primary transition-colors hover:border-gold hover:text-gold"
                >
                  {n.nameTh}
                  <span className="mt-1 block text-xs font-normal text-muted-foreground">
                    {BRANCH_LABEL[n.nearestBranch]}
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              {crossLinks.map((n) =>
                PROVINCE_SERVICE_SLUGS.slice(0, 2).map((slug) => (
                  <Link
                    key={`${n.slug}-${slug}`}
                    to="/provinces/$province/$service"
                    params={{ province: n.slug, service: slug }}
                    className="rounded-full border border-border bg-muted/30 px-3 py-1.5 text-muted-foreground hover:border-gold hover:text-gold"
                  >
                    {PROVINCE_SERVICES[slug].label} {n.nameTh}
                  </Link>
                )),
              )}
            </div>
          </div>
        </section>

        <section className="defer-paint border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">ทำไมลูกค้า{p.nameTh}เลือก Thai Notary</h2>
            <ul className="mt-4 grid gap-3 md:grid-cols-2">
              {[
                `ทีมทนาย Notarial Services Attorney ขึ้นทะเบียนสภาทนายความฯ — MFA และสถานทูตทุกประเทศยอมรับ`,
                `${BRANCH_LABEL[p.nearestBranch]}ดูแลโซนของคุณ — ตอบไลน์ภายใน 30 นาที (08:00–20:00 ทุกวัน)`,
                `ส่งเอกสารผ่าน Kerry/Flash มีประกันพัสดุและ tracking ทุกครั้ง`,
                `รองรับ 30+ ภาษา — อังกฤษ จีน ญี่ปุ่น เกาหลี เยอรมัน ฝรั่งเศส สเปน ฯลฯ`,
                `เคสด่วน Same-Day Notary + ส่ง Flash Express ในวันเดียวกัน`,
                `${COMPANY.trustLine} — ใบเสร็จ/ใบกำกับภาษีเต็มรูปแบบ`,
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="defer-paint border-b border-border bg-muted/20">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <h2 className="text-2xl font-bold">คำถามที่พบบ่อยจากลูกค้า{p.nameTh}</h2>
            <div className="mt-5 space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer font-semibold text-primary">{f.q}</summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">พร้อมเริ่มงานที่{p.nameTh}แล้ว?</h2>
            <p className="mt-3 text-sm opacity-90">
              ทักไลน์ส่งภาพเอกสาร — ทีมประเมินภายใน 30 นาที พร้อมแจ้งเวลาส่งคืน
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground">
                โทร {COMPANY.phone}
              </a>
              <a href={`https://line.me/R/ti/p/${COMPANY.lineId}`} target="_blank" rel="noopener" className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold">
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
